import React, {useEffect} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import WizardSectionCard from './sections/WizardSectionCard'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {toOptionalNumeric} from '../wizard/wizardAccess'
import {
  getRetentionPreferenceText,
  normalizeRetentionPreference,
  RETENTION_PREFERENCE_OPTIONS,
} from '../../models/retention-preference'

interface PrivacyStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
}

const PrivacyStep: React.FC<PrivacyStepProps> = ({register, errors, control, setValue, watch}) => {
  const retentionPreference = watch('retentionPreference')
  const retentionPreferenceText = watch('retentionPreferenceText')
  const selectedRetention = normalizeRetentionPreference(toOptionalNumeric(retentionPreference, 1))

  useEffect(() => {
    if (!selectedRetention) {
      return
    }

    const defaultText = getRetentionPreferenceText(selectedRetention)
    if (retentionPreference !== selectedRetention) {
      setValue('retentionPreference', selectedRetention, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }

    if (!retentionPreferenceText?.trim() && defaultText) {
      setValue('retentionPreferenceText', defaultText, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }
  }, [retentionPreference, retentionPreferenceText, selectedRetention, setValue])

  const chooseRetention = (value: number) => {
    setValue('retentionPreferenceSelection', value, {shouldDirty: true})
    setValue('retentionPreference', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })

    const text = getRetentionPreferenceText(value)
    setValue('retentionPreferenceText', text, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  return (
    <div>
      <div className='d-grid gap-4'>
        <WizardSectionCard title='How long should Doora keep your card?'>
          <RadioButtonInput<IApplicationFormData>
            name='retentionPreference'
            label=''
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={RETENTION_PREFERENCE_OPTIONS}
            value={selectedRetention}
            defaultValue={selectedRetention}
            onChange={chooseRetention}
            itemClassName='mt-5 mb-5 fs-4'
            labelClassName='m'
          />

          {errors?.retentionPreference && (
            <div className='text-danger small mt-2'>
              {String(errors.retentionPreference.message)}
            </div>
          )}
        </WizardSectionCard>

        <WizardSectionCard title='No matter what you pick:' className='doora-bg px-10 py-8'>
          <ul className='wizard-check-list mt-5'>
            <li className='fs-6'>
              <i className='bi bi-check-circle check-icon'></i>
              Revoke any agent's access from your dashboard
            </li>
            <li className='fs-6'>
              <i className='bi bi-check-circle check-icon'></i>
              Delete your entire Doora Card anytime
            </li>
          </ul>
        </WizardSectionCard>
      </div>
    </div>
  )
}

export default PrivacyStep
